import React, { useState } from "react";
import {
  Box,
  ClickAwayListener,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";

import { flexRender, Header } from "@tanstack/react-table";
import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
  InfoIcon,
} from "lucide-react";

import { VtColumnMeta } from "./DataTable";

function getSortIcons() {
  return {
    asc: <ArrowUpIcon size={12} style={{ minWidth: 12, minHeight: 12 }} />,
    desc: <ArrowDownIcon size={12} style={{ minWidth: 12, minHeight: 12 }} />,
    default: (
      <Box sx={{ lineHeight: 0.5, color: "backgrounds.accent.strong.default" }}>
        <ArrowUpDownIcon size={12} style={{ minWidth: 12, minHeight: 12 }} />
      </Box>
    ),
  };
}

const HeaderCell = <TData,>({ header }: { header: Header<TData, unknown> }) => {
  const sortIcons = getSortIcons();
  const [open, setOpen] = useState(false);

  const meta = header.column.columnDef.meta as VtColumnMeta;

  const tooltip = meta?.tooltip;
  const isSortable = header.column.getCanSort();
  const sortIcon = sortIcons[header.column.getIsSorted() || "default"];

  const toggleTooltip = () => setOpen((open) => !open);

  return (
    <TableCell
      sx={{
        borderRightWidth: header.column.getIsLastColumn() ? 0 : 1,
        borderRightStyle: "solid",
        borderColor: "backgrounds.input.default",
        backgroundColor: "backgrounds.neutral.soft.default",
        padding: 1.5,
        paddingLeft: 2.5,
        verticalAlign: "top",
      }}
    >
      {header.isPlaceholder ? null : (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Tooltip placement="top" open={open} title={tooltip}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                cursor: isSortable || tooltip ? "pointer" : "default",
                gap: 0.5,
                color: "texts.neutral.subtle",
              }}
              onClick={(event) => {
                if (isSortable) {
                  header.column.getToggleSortingHandler()?.(event);
                } else if (tooltip) {
                  toggleTooltip();
                }
              }}
            >
              <Typography
                variant={"bodyMicroRegular"}
                sx={{ textTransform: "uppercase" }}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </Typography>

              {tooltip && (
                <InfoIcon size={18} style={{ minWidth: 18, minHeight: 18 }} />
              )}

              {isSortable && !tooltip && sortIcon}
            </Box>
          </Tooltip>
        </ClickAwayListener>
      )}
    </TableCell>
  );
};

export default HeaderCell;
